/**
 * Gölge atama — `GET /api/integrations/shadow-assign` (salt okunur).
 *
 * Panelin (Hayat/Abant) atadığı araç ile bugünkü bölge/sokak kurallarının vereceği araç yan yana
 * gelir; uyum oranı sunucuda hesaplanır. Bu modül yalnız istemcidir; kurallar
 * `apps/api/src/integrations/fleet/shadow-assign.service.ts` içindedir. Yazma yok.
 *
 * Yanıt tipleri sunucu sözleşmesiyle birebir. Oran `ratePct` sunucudan gelir; istemci hesap yapmaz.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export type ShadowSource = 'HAYAT_PANEL' | 'ABANT_PANEL';
export type ShadowVia = 'STREET_VEHICLE' | 'STREET_REGION' | 'NEIGHBORHOOD';

export interface ShadowVehicleRef {
  id: string;
  plate: string;
}

export interface ShadowBucket {
  total: number;
  comparable: number;
  agree: number;
  ratePct: number | null;
}

export interface ShadowSummary extends ShadowBucket {
  bySource: Record<ShadowSource, ShadowBucket>;
}

export interface ShadowRegionRow extends ShadowBucket {
  region: { id: string; name: string } | null;
}

/** Şoförün kimliği: ad ve sürücü id'si. Telefon/e-posta sunucuda hiç dönmez. */
export interface ShadowPerson {
  driverId: string;
  name: string;
}

/** Kişi bazlı karşılaştırma: gruplama anahtarı PANEL tarafındaki kişidir. */
export interface ShadowPersonRow {
  driver: { id: string; name: string } | null;
  d7: ShadowBucket;
  d30: ShadowBucket;
}

export interface ShadowRow {
  orderId: string;
  source: ShadowSource;
  receivedAt: string;
  panel: {
    label: string;
    displayName: string | null;
    vehicle: ShadowVehicleRef | null;
    /** Panelin verdiği şoför (Abant'ta driverMap, yoksa aracın zimmetli şoförü). */
    person: ShadowPerson | null;
  };
  ours: {
    vehicle: ShadowVehicleRef | null;
    via: ShadowVia | null;
    region: { id: string; name: string } | null;
    /** Bizim kuralımızın vereceği aracın zimmetli şoförü. */
    person: ShadowPerson | null;
  };
  /** İki taraf da araç verdiyse eşitlik; biri boşsa null (karşılaştırılamaz). */
  agree: boolean | null;
}

export interface ShadowAssignResponse {
  days: number;
  summary: { d7: ShadowSummary; d30: ShadowSummary };
  byRegion: ShadowRegionRow[];
  byPerson: ShadowPersonRow[];
  rows: ShadowRow[];
  truncated?: true;
}

/**
 * Gölge atama sorgusu.
 *
 * `days` 7 veya 30. `orderIds` verilirse `rows` yalnız o siparişlerdir (sunucu 200'e kadar kabul
 * eder). `orderIds` boşsa istek atılmaz (`enabled` false). Yenileme 60 sn.
 */
export function useShadowAssign(opts: {
  days: 7 | 30;
  orderIds?: string[];
  enabled?: boolean;
  /** false: periyodik yenileme kapalı (Orders); varsayılan 60 sn (kart). */
  poll?: boolean;
}) {
  const params = new URLSearchParams();
  params.set('days', String(opts.days));
  if (opts.orderIds && opts.orderIds.length > 0) {
    params.set('orderIds', opts.orderIds.join(','));
  }
  const url = `/api/integrations/shadow-assign?${params.toString()}`;
  const hasIds = opts.orderIds === undefined || opts.orderIds.length > 0;
  return useQuery({
    queryKey: ['shadow-assign', opts.days, opts.orderIds?.join(',') ?? null],
    queryFn: () => api.get<ShadowAssignResponse>(url),
    refetchInterval: opts.poll === false ? false : 60_000,
    enabled: (opts.enabled ?? true) && hasIds,
  });
}
